import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { translate } from '@ngneat/transloco';

export interface MarkdownExtraAction {
    icon: string; // Material icon name
    title: string; // Tooltip text
    type?: 'ATTACHMENT' | 'BUTTON'; // Type of action
    action: (...args: any) => void; // Callback function
    disabled?: boolean; // Optional disabled state
}

interface PlaceholderItem {
    value: string;
    label: string;
    description: string;
}

@Component({
    selector: 'markdown-comment-input',
    templateUrl: './markdown-comment-input.component.html',
    styleUrls: ['./markdown-comment-input.component.scss'],
    standalone: false,
})
export class MarkdownCommentInputComponent implements OnInit, OnChanges {
    @ViewChild('contentTextarea') contentTextarea!: ElementRef<HTMLTextAreaElement>;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    @Input() content = '';
    @Input() placeholder: string = translate('Write your content... (Markdown supported)');
    @Input() rows = 3;
    @Input() disabled = false;
    @Input() enableEncoding = true; // Enable/disable encoding
    @Input() collapsedPlaceholder: string = translate('Write your content... (Markdown supported)');
    @Input() enableScroll = false; // Enable/disable internal scrolling
    @Input() collapsedClass = ''; // Additional CSS classes for collapsed state
    @Input() collapsedContainerClass = ''; // Additional CSS classes for collapsed container
    @Input() enableTruncate = false; // Enable/disable truncation with hover to show full content
    @Input() extraActions: MarkdownExtraAction[] = []; // Extra actions from parent component
    @Input() commentActions: MarkdownExtraAction[] = []; // Extra actions from parent component
    @Input() expandInitially = false;
    @Input() isExpanded = false;

    @Output() contentChange = new EventEmitter<string>();
    @Output() onBlurEvent = new EventEmitter<string>();
    @Output() collapsedClick = new EventEmitter<MouseEvent>(); // Event emitted when collapsed div is clicked

    @Output() postClick = new EventEmitter<void>();
    @Output() attachClick = new EventEmitter<any>();

    public showPreview = false;
    public markdownPreview: SafeHtml = '<em class="text-gray-400">Nothing to preview</em>';
    public collapsedPreview: SafeHtml = ''; // Preview for collapsed state
    public decodedContent = ''; // Internal decoded value for editing

    // History management
    private history: string[] = [];
    private historyIndex = -1;
    public canUndo = false;
    public canRedo = false;

    // Active formatting states
    public isInList = false;
    public isInBulletedList = false;
    public isInNumberedList = false;
    public listMode: 'bulleted' | 'numbered' | null = null;
    public isBold = false;
    public isItalic = false;
    public isUnderline = false;
    public isStrikethrough = false;
    public isCode = false;
    public isHighlight = false;
    public isColored = false;
    public isLink = false;
    public isQuote = false;

    // Table selector
    public showTableSelector = false;
    public tableRows = 3;
    public tableCols = 3;
    public hoveredTableCell: { row: number; col: number } | null = null;
    public tableSelectorPosition: { top: number; left: number } = { top: 0, left: 0 };

    // Color pickers
    public showHighlightColorPicker = false;
    public showTextColorPicker = false;
    public suggestedColors: string[] = [
        '#ffeb3b',
        '#ff9800',
        '#f44336',
        '#e91e63',
        '#9c27b0',
        '#673ab7',
        '#3f51b5',
        '#2196f3',
        '#00bcd4',
        '#009688',
        '#4caf50',
        '#8bc34a',
    ];
    public textColors: string[] = [
        '#000000',
        '#424242',
        '#616161',
        '#757575',
        '#9e9e9e',
        '#d81844',
        '#ff0000',
        '#ff5722',
        '#ff9800',
        '#ffc107',
        '#00a651',
        '#4caf50',
        '#2196f3',
        '#1976d2',
        '#673ab7',
        '#9c27b0',
    ];
    public selectedHighlightColor = '#ffeb3b';
    public selectedTextColor = '#ff0000';
    public currentHighlightColor = '';
    public currentTextColor = '';

    public availablePlaceholders: PlaceholderItem[] = [
        { value: '{year(now)}', label: 'Current Year', description: 'e.g. 2025' },
        { value: '{month(now)}', label: 'Current Month', description: 'e.g. June' },
        { value: '{day(now)}', label: 'Current Day', description: 'e.g. 25' },
        { value: '{weekday(now)}', label: 'Current Weekday', description: 'e.g. Wednesday' },
        { value: '{today}', label: "Today's Date", description: 'e.g. 2025-06-25' },
        { value: '{now}', label: 'Current Timestamp', description: 'e.g. 2025-06-25T14:30:05Z07:00' },
        { value: '{task.dueDate.Format("Jan-06")}', label: 'Task Due Date', description: 'e.g. Jun-25' },
    ];
    public selectedPlaceholderIndex = -1;
    public menuPosition = { x: 0, y: 0 };

    @ViewChild('placeholdersTrigger') placeholdersTrigger: MatMenuTrigger;

    @HostListener('keydown.control.space', ['$event'])
    @HostListener('keydown.meta.space', ['$event']) // For Mac's Command key
    handleKeyboardEvent(event: Event) {
        if (this.contentTextarea?.nativeElement && document.activeElement === this.contentTextarea?.nativeElement) {
            event.preventDefault();
            event.stopPropagation();
            const textarea = this.contentTextarea?.nativeElement;
            textarea.focus({ preventScroll: true });
            const rect = textarea.getBoundingClientRect();
            this.menuPosition.y = rect.bottom + rect.height - 20;
            requestAnimationFrame(() => {
                this.openPlaceholdersMenu();
            });
        }
    }

    constructor(private sanitizer: DomSanitizer, private renderer: Renderer2) {}

    // Open the placeholders menu
    openPlaceholdersMenu() {
        if (!this.placeholdersTrigger) return;

        // Close menu first with a small delay
        this.placeholdersTrigger.closeMenu();

        // Use a minimal timeout to ensure the close completes
        setTimeout(() => {
            try {
                this.placeholdersTrigger.openMenu();
            } catch {
                // If opening fails, try one more time after a short delay
                setTimeout(() => this.placeholdersTrigger?.openMenu(), 50);
            }
        }, 10);
    }

    // Handle keyboard navigation in placeholders menu
    onPlaceholderKeydown(event: KeyboardEvent, index: number, direction: 'up' | 'down') {
        event.preventDefault();
        event.stopPropagation();

        const items = this.availablePlaceholders;
        let newIndex = index;

        if (direction === 'down') {
            newIndex = index < items.length - 1 ? index + 1 : 0;
        } else {
            newIndex = index > 0 ? index - 1 : items.length - 1;
        }

        this.selectedPlaceholderIndex = newIndex;

        // Scroll the selected item into view
        setTimeout(() => {
            const selectedItem = document.querySelector(`.placeholder-item[data-index="${newIndex}"]`);
            if (selectedItem) {
                selectedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                // Focus the selected item for better accessibility
                (selectedItem as HTMLElement).focus();
            }
        });
    }

    // Handle menu opened event
    onMenuOpened() {
        // Initialize the selected index when menu opens
        if (this.availablePlaceholders.length > 0) {
            this.selectedPlaceholderIndex = 0;
        }
    }

    // Handle menu closed event
    onMenuClosed() {
        // Reset the selected index when menu closes
        this.selectedPlaceholderIndex = -1;
    }

    // Handle keyboard events for placeholders
    onPlaceholderKeyEvent(event: Event, index: number, type: 'up' | 'down') {
        const keyboardEvent = event as KeyboardEvent;
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        this.onPlaceholderKeydown(keyboardEvent, index, type);
    }

    // Add this method to insert placeholder
    insertPlaceholder(placeholder: string, index = -1) {
        const textarea = this.contentTextarea?.nativeElement;
        this.selectedPlaceholderIndex = index; // Store the selected index

        // Close the menu first
        if (this.placeholdersTrigger) {
            this.placeholdersTrigger.closeMenu();
        }

        if (index === -2) {
            textarea.focus();
            return;
        }

        // Use requestAnimationFrame to ensure the menu is closed
        requestAnimationFrame(() => {
            if (!textarea) return;

            // Save current selection
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = textarea.value || '';

            // Insert the placeholder
            const newText = text.substring(0, start) + placeholder + text.substring(end);
            this.decodedContent = newText;

            // Update the model
            this.content = this.encodeContent(this.decodedContent);
            this.contentChange.emit(this.content);

            // Update cursor position
            requestAnimationFrame(() => {
                if (textarea) {
                    const newPos = start + placeholder.length;
                    textarea.focus();
                    textarea.setSelectionRange(newPos, newPos);
                    this.updatePreview();
                    this.updateFormattingStates();
                }
            });
        });
    }

    ngOnInit() {
        this.decodedContent = this.decodeContent(this.content);
        // Update collapsed preview
        this.updateCollapsedPreview();

        // Auto-resize textarea after view initialization if content exists and scroll is disabled
        if (this.decodedContent && !this.enableScroll) {
            setTimeout(() => this.autoResizeTextarea(), 0);
        }

        if (this.expandInitially) {
            this.expandEditor();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['content']) {
            if (changes['content'].currentValue !== changes['content'].previousValue) {
                this.onContentChange(this.decodeContent(changes['content'].currentValue), true);
            }
        }
    }

    expandEditor() {
        if (!this.disabled) {
            this.isExpanded = true;
            setTimeout(() => {
                this.contentTextarea?.nativeElement?.focus();
                // Auto-resize textarea when expanding if scroll is disabled
                if (!this.enableScroll) {
                    this.autoResizeTextarea();
                }
            }, 0);
        }
    }

    onCollapsedClick(event: MouseEvent) {
        // Emit the click event to parent
        this.collapsedClick.emit(event);

        // Expand editor if not disabled
        if (!this.disabled) {
            this.expandEditor();
        }
    }

    onContentChange(value: string, dontEmitEvent?: boolean, event?: any) {
        this.decodedContent = value;
        const outputValue = this.encodeContent(value);
        this.content = outputValue;
        if (!dontEmitEvent) {
            this.contentChange.emit(outputValue);
        }
        this.updatePreview();
        this.updateCollapsedPreview();
        this.addToHistory(value);
        this.updateFormattingStates();

        // Auto-resize textarea if scroll is disabled
        if (!this.enableScroll) {
            setTimeout(() => this.autoResizeTextarea(), 0);
        }
        const textarea = this.contentTextarea?.nativeElement;
        textarea?.focus({ preventScroll: true });
        const rect = textarea?.getBoundingClientRect();
        this.menuPosition.y = rect?.bottom + rect?.height - 20;
        if (event && event === '{') {
            this.openPlaceholdersMenu();
        } else if (event && value.endsWith('{')) {
            this.openPlaceholdersMenu();
        }
    }

    private autoResizeTextarea() {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        // Reset height to auto to get the correct scrollHeight
        this.renderer.setStyle(textarea, 'height', 'auto');
        // Set height to scrollHeight to fit content
        this.renderer.setStyle(textarea, 'height', textarea.scrollHeight + 'px');
    }

    onPreviewToggle() {
        if (this.showPreview) {
            this.updatePreview();
        }
    }

    insertMarkdown(before: string, after: string) {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const selectedText = text.substring(start, end);

        const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
        this.decodedContent = newText;

        // Encode before emitting if encoding is enabled
        const outputValue = this.encodeContent(newText);
        this.content = outputValue;
        this.contentChange.emit(outputValue);

        // Set cursor position
        setTimeout(() => {
            const newPos = start + before.length + selectedText.length;
            textarea.focus();
            textarea.setSelectionRange(newPos, newPos);
        });

        this.updatePreview();
        this.addToHistory(this.decodedContent);
        this.updateFormattingStates();

        // Auto-resize textarea if scroll is disabled
        if (!this.enableScroll) {
            setTimeout(() => this.autoResizeTextarea(), 0);
        }
    }

    private encodeContent(text: string): string {
        // Base64 encode the content text
        if (this.enableEncoding) {
            try {
                return btoa(unescape(encodeURIComponent(text)));
            } catch (e) {
                console.error('Error encoding content:', e);
                return text;
            }
        } else {
            return text;
        }
    }

    private decodeContent(encodedText: string): string {
        // Decode initial value if encoding is enabled
        if (encodedText && this.enableEncoding) {
            // Check if the string is valid base64
            if (!this.isValidBase64(encodedText)) {
                // Not base64, return as-is (it's already plain text)
                return encodedText;
            }

            try {
                return decodeURIComponent(escape(atob(encodedText)));
            } catch (e) {
                console.error('Error decoding content:', e);
                // If decoding fails, return the original text
                return encodedText;
            }
        } else {
            return encodedText || '';
        }
    }

    private isValidBase64(str: string): boolean {
        if (!str || str.length === 0) {
            return false;
        }

        // Base64 strings should only contain A-Z, a-z, 0-9, +, /, and = (for padding)
        const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;

        // Check if string matches base64 pattern
        if (!base64Regex.test(str)) {
            return false;
        }

        // Base64 length should be divisible by 4
        if (str.length % 4 !== 0) {
            return false;
        }

        // Try to decode to verify it's valid base64
        try {
            atob(str);
            return true;
        } catch {
            return false;
        }
    }

    private parseMarkdown(text: string): string {
        let html = text;

        // Headers
        html = html.replace(/^### (.*$)/gim, '<h3 class="text-base mb-1.5 font-semibold">$1</h3>');
        html = html.replace(/^## (.*$)/gim, '<h2 class="text-lg mb-2 font-semibold">$1</h2>');
        html = html.replace(/^# (.*$)/gim, '<h1 class="text-xl mb-2.5 border-b-2 border-gray-200 pb-1.5 font-semibold">$1</h1>');

        // Bold + Italic (must come before individual bold/italic)
        html = html.replace(/\*\*\*(.*?)\*\*\*/gim, '<strong class="font-semibold"><em class="italic">$1</em></strong>');

        // Bold
        html = html.replace(/\*\*(.*?)\*\*/gim, '<strong class="font-semibold">$1</strong>');

        // Italic
        html = html.replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>');

        // Strikethrough
        html = html.replace(/~~(.*?)~~/gim, '<del class="line-through">$1</del>');

        // Inline code
        html = html.replace(/`(.*?)`/gim, '<code class="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-[13px]">$1</code>');

        // Links - with highlight background, no underline
        html = html.replace(
            /\[([^\]]+)\]\(([^\)]+)\)/gim,
            '<a href="$2" class="text-blue-600 group-hover:!text-white px-1 py-0.5 rounded no-underline" target="_blank" (click)="event.stopPropagation()">$1</a>'
        );

        // Blockquotes - better styling
        html = html.replace(
            /^&gt; (.*$)/gim,
            '<blockquote class="border-l-4 border-blue-500 px-4 py-3 my-2.5 bg-blue-50 text-blue-800 italic rounded">$1</blockquote>'
        );
        html = html.replace(
            /^> (.*$)/gim,
            '<blockquote class="border-l-4 border-blue-500 px-4 py-3 my-2.5 bg-blue-50 text-blue-800 italic rounded">$1</blockquote>'
        );

        // Horizontal rules (must be before line breaks conversion)
        html = html.replace(/^---$/gim, '<hr class="border-0 border-t-2 border-gray-200 my-4">');
        html = html.replace(/^\*\*\*$/gim, '<hr class="border-0 border-t-2 border-gray-200 my-4">');

        // Line breaks (convert early so we can process line by line)
        html = html.replace(/\n/gim, '<br>');

        // Tables - convert markdown tables to HTML tables (must come before list processing)
        html = this.convertMarkdownTables(html);

        // Unordered lists - handle indentation with nested structure
        const lines = html.split('<br>');
        const listStack: { level: number; type: string }[] = [];
        let listHtml = '';

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const indentMatch = line.match(/^(\s*)- (.*)$/);

            if (indentMatch) {
                const indentSpaces = indentMatch[1].length;
                const content = indentMatch[2];
                const level = Math.floor(indentSpaces / 2); // 2 spaces = 1 level

                // Close lists if we've decreased indent
                while (listStack.length > 0 && listStack[listStack.length - 1].level >= level) {
                    listHtml += '</ul>';
                    listStack.pop();
                }

                // Open new list if needed
                if (listStack.length === 0 || listStack[listStack.length - 1].level < level) {
                    listHtml += '<ul class="list-disc my-2 pl-6">';
                    listStack.push({ level, type: 'ul' });
                }

                listHtml += `<li class="my-1">${content}</li>`;
            } else {
                // Close all open lists
                while (listStack.length > 0) {
                    listHtml += '</ul>';
                    listStack.pop();
                }
                listHtml += line;
            }

            if (i < lines.length - 1 && !indentMatch) {
                listHtml += '<br>';
            }
        }

        // Close any remaining open lists
        while (listStack.length > 0) {
            listHtml += '</ul>';
            listStack.pop();
        }

        html = listHtml;

        return html;
    }

    updatePreview() {
        if (!this.showPreview) return;

        const text = this.decodedContent?.trim() || '';
        if (text === '') {
            this.markdownPreview = this.sanitizer.sanitize(1, '<em class="text-gray-400">Nothing to preview</em>') || '';
        } else {
            const parsedHtml = this.parseMarkdown(text);
            // Bypass sanitization to preserve inline styles for user-selected colors
            this.markdownPreview = this.sanitizer.bypassSecurityTrustHtml(parsedHtml);
        }
    }

    private updateCollapsedPreview() {
        const text = this.decodedContent?.trim() || '';
        if (text === '') {
            this.collapsedPreview = this.sanitizer.sanitize(1, this.collapsedPlaceholder) || '';
        } else {
            const parsedHtml = this.parseMarkdown(text);
            // Bypass sanitization to preserve inline styles for user-selected colors
            this.collapsedPreview = this.sanitizer.bypassSecurityTrustHtml(parsedHtml);
        }
    }

    private convertMarkdownTables(html: string): string {
        // Match markdown tables - need to handle <br> tags
        const lines = html.split('<br>');
        let result = '';
        let i = 0;

        while (i < lines.length) {
            const line = lines[i].trim();

            // Check if this line starts a table
            if (line.startsWith('|') && line.endsWith('|')) {
                // Found potential table start
                const headerLine = line;

                // Check if next line is separator
                if (i + 1 < lines.length) {
                    const separatorLine = lines[i + 1].trim();
                    if (separatorLine.match(/^\|[\s\-:|]+\|$/)) {
                        // This is a table!
                        let tableHtml = '<table class="border-collapse w-full my-2.5 border-2 border-gray-300">';

                        // Parse header
                        const headerCells = headerLine.split('|').filter(cell => cell.trim());
                        tableHtml += '<thead><tr>';
                        headerCells.forEach(cell => {
                            tableHtml += `<th class="border border-gray-300 p-2.5 font-semibold text-left">${cell.trim()}</th>`;
                        });
                        tableHtml += '</tr></thead>';

                        // Parse body rows
                        tableHtml += '<tbody>';
                        i += 2; // Skip header and separator

                        while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                            const cells = lines[i]
                                .trim()
                                .split('|')
                                .filter(cell => cell.trim());
                            if (cells.length > 0) {
                                tableHtml += '<tr>';
                                cells.forEach(cell => {
                                    tableHtml += `<td class="border border-gray-300 p-2.5">${cell.trim()}</td>`;
                                });
                                tableHtml += '</tr>';
                            }
                            i++;
                        }

                        tableHtml += '</tbody></table>';
                        result += tableHtml;
                        continue;
                    }
                }
            }

            // Not a table, add line as-is
            result += line;
            if (i < lines.length - 1) {
                result += '<br>';
            }
            i++;
        }

        return result;
    }

    // Color operations
    toggleHighlightColorPicker() {
        this.showHighlightColorPicker = !this.showHighlightColorPicker;
        this.showTextColorPicker = false;
        this.showTableSelector = false;
    }

    toggleTextColorPicker() {
        this.showTextColorPicker = !this.showTextColorPicker;
        this.showHighlightColorPicker = false;
        this.showTableSelector = false;
    }

    applyHighlightColor(color: string) {
        this.selectedHighlightColor = color;
        // Note: Using inline style for dynamic user-selected colors since Tailwind can't handle runtime color values
        this.insertMarkdown(`<mark class="bg-[${color}]">`, '</mark>');
        this.showHighlightColorPicker = false;
    }

    applyTextColor(color: string) {
        this.selectedTextColor = color;
        // Note: Using inline style for dynamic user-selected colors since Tailwind can't handle runtime color values
        this.insertMarkdown(`<span class="text-[${color}]">`, '</span>');
        this.showTextColorPicker = false;
    }

    // List operations
    insertList(type: 'bulleted' | 'numbered') {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const hasSelection = start !== end;

        if (hasSelection) {
            // Apply list to selected lines
            const selectedText = text.substring(start, end);
            const lines = selectedText.split('\n');
            const prefix = type === 'bulleted' ? '- ' : '';

            const formattedLines = lines.map((line, index) => {
                if (line.trim()) {
                    if (type === 'numbered') {
                        return `${index + 1}. ${line}`;
                    } else {
                        return `${prefix}${line}`;
                    }
                }
                return line;
            });

            const newText = text.substring(0, start) + formattedLines.join('\n') + text.substring(end);
            this.decodedContent = newText;
            this.listMode = null; // Clear list mode after applying to selection
        } else {
            // Toggle list mode for continuous typing
            if (this.listMode === type) {
                // Turn off list mode
                this.listMode = null;
            } else {
                // Turn on list mode
                this.listMode = type;
                const lines = text.split('\n');
                const currentLine = text.substring(0, start).split('\n').length - 1;

                // Add list marker to current line if it doesn't have one
                const line = lines[currentLine];
                const prefix = type === 'bulleted' ? '- ' : '1. ';
                if (!line.startsWith(prefix)) {
                    lines[currentLine] = prefix + line;
                    this.decodedContent = lines.join('\n');
                }
            }
        }

        const outputValue = this.encodeContent(this.decodedContent);
        this.content = outputValue;
        this.contentChange.emit(outputValue);
        this.updatePreview();
        this.addToHistory(this.decodedContent);
        this.updateFormattingStates();

        // Auto-resize textarea if scroll is disabled
        if (!this.enableScroll) {
            setTimeout(() => this.autoResizeTextarea(), 0);
        }
    }

    // Update formatting states based on cursor position
    private updateFormattingStates() {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const lines = text.split('\n');
        const currentLineIndex = text.substring(0, start).split('\n').length - 1;
        const currentLine = lines[currentLineIndex];

        // Check if in list - must have content after the marker
        this.isInBulletedList = /^\s*-\s+\S/.test(currentLine);
        this.isInNumberedList = /^\s*\d+\.\s+\S/.test(currentLine);
        this.isInList = this.isInBulletedList || this.isInNumberedList;

        // If list mode is active but current line doesn't have list marker, deactivate
        if (this.listMode === 'bulleted' && !this.isInBulletedList) {
            this.listMode = null;
        }
        if (this.listMode === 'numbered' && !this.isInNumberedList) {
            this.listMode = null;
        }

        // Check for other formatting at cursor position
        const selectedText = text.substring(Math.max(0, start - 100), Math.min(text.length, end + 100));
        const cursorOffset = Math.min(100, start);

        // Check if cursor is within formatted text
        this.isBold = this.isWithinFormat(selectedText, cursorOffset, '**', '**');
        this.isItalic = this.isWithinFormat(selectedText, cursorOffset, '*', '*') && !this.isBold;
        this.isUnderline = this.isWithinFormat(selectedText, cursorOffset, '<u>', '</u>');
        this.isStrikethrough = this.isWithinFormat(selectedText, cursorOffset, '~~', '~~');
        this.isCode = this.isWithinFormat(selectedText, cursorOffset, '`', '`');

        // Extract actual highlight color if within highlight
        const highlightMatch = this.extractColorFromFormat(selectedText, cursorOffset, '<mark', '</mark>');
        this.isHighlight = highlightMatch.found;
        this.currentHighlightColor = highlightMatch.color || '#ffeb3b';

        // Extract actual text color if within colored text
        const colorMatch = this.extractColorFromFormat(selectedText, cursorOffset, '<span class="text-[', '</span>');
        this.isColored = colorMatch.found;
        this.currentTextColor = colorMatch.color || '#ff0000';

        this.isLink = this.isWithinFormat(selectedText, cursorOffset, '[', '](');
        this.isQuote = /^\s*>\s/.test(currentLine);
    }

    private extractColorFromFormat(
        text: string,
        cursorPos: number,
        startMarker: string,
        endMarker: string
    ): { found: boolean; color: string } {
        let pos = 0;
        while (pos < text.length) {
            const startIdx = text.indexOf(startMarker, pos);
            if (startIdx === -1 || startIdx > cursorPos) break;

            const endIdx = text.indexOf(endMarker, startIdx + startMarker.length);
            if (endIdx === -1) break;

            if (cursorPos >= startIdx && cursorPos <= endIdx + endMarker.length) {
                // Extract color from the tag
                const tagContent = text.substring(startIdx, endIdx + endMarker.length);
                const colorMatch = tagContent.match(/(#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3})/);
                return { found: true, color: colorMatch ? colorMatch[1] : '' };
            }
            pos = endIdx + endMarker.length;
        }
        return { found: false, color: '' };
    }

    private isWithinFormat(text: string, cursorPos: number, startMarker: string, endMarker: string): boolean {
        let pos = 0;
        while (pos < text.length) {
            const startIdx = text.indexOf(startMarker, pos);
            if (startIdx === -1 || startIdx > cursorPos) break;

            // For bold, need to ensure we're looking for ** not just *
            if (startMarker === '**') {
                // Check if this is actually a bold marker (not part of italic)
                const charBefore = startIdx > 0 ? text[startIdx - 1] : '';
                const charAfter = startIdx + 2 < text.length ? text[startIdx + 2] : '';
                // Skip if this is part of a longer sequence of asterisks
                if (charBefore === '*' || charAfter === '*') {
                    pos = startIdx + 1;
                    continue;
                }
            }

            // For italic, skip if this is part of bold
            if (startMarker === '*' && endMarker === '*') {
                const charBefore = startIdx > 0 ? text[startIdx - 1] : '';
                const charAfter = startIdx + 1 < text.length ? text[startIdx + 1] : '';
                if (charBefore === '*' || charAfter === '*') {
                    pos = startIdx + 1;
                    continue;
                }
            }

            const endIdx = text.indexOf(endMarker, startIdx + startMarker.length);
            if (endIdx === -1) break;

            // For bold, verify end marker is also **
            if (startMarker === '**') {
                const charBefore = endIdx > 0 ? text[endIdx - 1] : '';
                const charAfter = endIdx + 2 < text.length ? text[endIdx + 2] : '';
                if (charBefore === '*' || charAfter === '*') {
                    pos = endIdx + 1;
                    continue;
                }
            }

            // For italic, verify end marker is single *
            if (startMarker === '*' && endMarker === '*') {
                const charBefore = endIdx > 0 ? text[endIdx - 1] : '';
                const charAfter = endIdx + 1 < text.length ? text[endIdx + 1] : '';
                if (charBefore === '*' || charAfter === '*') {
                    pos = endIdx + 1;
                    continue;
                }
            }

            if (cursorPos >= startIdx && cursorPos <= endIdx + endMarker.length) {
                return true;
            }
            pos = endIdx + endMarker.length;
        }
        return false;
    }

    // Indent operations
    changeIndent(direction: 'increase' | 'decrease') {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const text = textarea.value;
        const lines = text.split('\n');
        const currentLine = text.substring(0, start).split('\n').length - 1;

        if (direction === 'increase') {
            lines[currentLine] = '  ' + lines[currentLine];
        } else {
            lines[currentLine] = lines[currentLine].replace(/^  /, '');
        }

        this.decodedContent = lines.join('\n');
        const outputValue = this.encodeContent(this.decodedContent);
        this.content = outputValue;
        this.contentChange.emit(outputValue);
        this.updatePreview();
        this.addToHistory(this.decodedContent);

        // Auto-resize textarea if scroll is disabled
        if (!this.enableScroll) {
            setTimeout(() => this.autoResizeTextarea(), 0);
        }
    }

    // Paragraph
    insertParagraph() {
        this.insertMarkdown('\n\n', '\n\n');
        // Auto-resize is already handled in insertMarkdown
    }

    // Horizontal rule
    insertHorizontalRule() {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const text = textarea.value;
        const newText = text.substring(0, start) + '\n---\n' + text.substring(start);

        this.decodedContent = newText;
        const outputValue = this.encodeContent(newText);
        this.content = outputValue;
        this.contentChange.emit(outputValue);
        this.updatePreview();
        this.addToHistory(this.decodedContent);

        // Auto-resize textarea if scroll is disabled
        if (!this.enableScroll) {
            setTimeout(() => this.autoResizeTextarea(), 0);
        }
    }

    // Table operations
    toggleTableSelector(event?: MouseEvent) {
        this.showTableSelector = !this.showTableSelector;
        this.showHighlightColorPicker = false;
        this.showTextColorPicker = false;

        if (this.showTableSelector && event) {
            // Calculate position relative to button
            const target = event.currentTarget as HTMLElement;
            const rect = target.getBoundingClientRect();
            this.tableSelectorPosition = {
                top: rect.bottom + 4,
                left: rect.left,
            };
        }
    }

    onTableCellHover(row: number, col: number) {
        this.hoveredTableCell = { row, col };
    }

    insertTableWithSize(rows: number, cols: number) {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        // Generate table
        let tableTemplate = '\n';

        // Header row
        tableTemplate += '|';
        for (let i = 1; i <= cols; i++) {
            tableTemplate += ` Header ${i} |`;
        }
        tableTemplate += '\n';

        // Separator row
        tableTemplate += '|';
        for (let i = 0; i < cols; i++) {
            tableTemplate += '----------|';
        }
        tableTemplate += '\n';

        // Data rows
        for (let r = 1; r <= rows; r++) {
            tableTemplate += '|';
            for (let c = 1; c <= cols; c++) {
                tableTemplate += ` Cell ${r},${c} |`;
            }
            tableTemplate += '\n';
        }

        const start = textarea.selectionStart;
        const text = textarea.value;
        const newText = text.substring(0, start) + tableTemplate + text.substring(start);

        this.decodedContent = newText;
        const outputValue = this.encodeContent(newText);
        this.content = outputValue;
        this.contentChange.emit(outputValue);
        this.updatePreview();
        this.addToHistory(this.decodedContent);

        this.showTableSelector = false;
        this.hoveredTableCell = null;

        // Auto-resize textarea if scroll is disabled
        if (!this.enableScroll) {
            setTimeout(() => this.autoResizeTextarea(), 0);
        }
    }

    insertTableRow() {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const text = textarea.value;
        const lines = text.split('\n');
        const currentLine = text.substring(0, start).split('\n').length - 1;

        // Find if we're in a table
        if (lines[currentLine].includes('|')) {
            const cellCount = lines[currentLine].split('|').length - 2;
            const newRow = '|' + ' Cell   |'.repeat(cellCount);
            lines.splice(currentLine + 1, 0, newRow);

            this.decodedContent = lines.join('\n');
            const outputValue = this.encodeContent(this.decodedContent);
            this.content = outputValue;
            this.contentChange.emit(outputValue);
            this.updatePreview();
            this.addToHistory(this.decodedContent);

            // Auto-resize textarea if scroll is disabled
            if (!this.enableScroll) {
                setTimeout(() => this.autoResizeTextarea(), 0);
            }
        }
    }

    deleteTableElement() {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const text = textarea.value;
        const lines = text.split('\n');
        const currentLine = text.substring(0, start).split('\n').length - 1;

        // Delete current line if it's a table row
        if (lines[currentLine].includes('|')) {
            lines.splice(currentLine, 1);

            this.decodedContent = lines.join('\n');
            const outputValue = this.encodeContent(this.decodedContent);
            this.content = outputValue;
            this.contentChange.emit(outputValue);
            this.updatePreview();
            this.addToHistory(this.decodedContent);
        }
    }

    // History operations
    private addToHistory(value: string) {
        // Remove any redo history
        this.history = this.history.slice(0, this.historyIndex + 1);
        this.history.push(value);
        this.historyIndex++;

        // Limit history to 50 items
        if (this.history.length > 50) {
            this.history.shift();
            this.historyIndex--;
        }

        this.updateHistoryButtons();
    }

    private updateHistoryButtons() {
        this.canUndo = this.historyIndex > 0;
        this.canRedo = this.historyIndex < this.history.length - 1;
    }

    undo() {
        if (this.canUndo) {
            this.historyIndex--;
            this.decodedContent = this.history[this.historyIndex];
            const outputValue = this.encodeContent(this.decodedContent);
            this.content = outputValue;
            this.contentChange.emit(outputValue);
            this.updatePreview();
            this.updateHistoryButtons();
        }
    }

    redo() {
        if (this.canRedo) {
            this.historyIndex++;
            this.decodedContent = this.history[this.historyIndex];
            const outputValue = this.encodeContent(this.decodedContent);
            this.content = outputValue;
            this.contentChange.emit(outputValue);
            this.updatePreview();
            this.updateHistoryButtons();
        }
    }

    // Clear operations
    clearFormatting() {
        const textarea = this.contentTextarea?.nativeElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const selectedText = text.substring(start, end);

        // Remove common markdown formatting
        const cleanText = selectedText
            .replace(/\*\*(.+?)\*\*/g, '$1') // Bold
            .replace(/\*(.+?)\*/g, '$1') // Italic
            .replace(/~~(.+?)~~/g, '$1') // Strikethrough
            .replace(/<u>(.+?)<\/u>/g, '$1') // Underline
            .replace(/<mark>(.+?)<\/mark>/g, '$1') // Highlight
            .replace(/<span[^>]*>(.+?)<\/span>/g, '$1') // Colored text
            .replace(/`(.+?)`/g, '$1') // Code
            .replace(/\[(.+?)\]\([^)]+\)/g, '$1') // Links
            .replace(/^[->]\s/gm, '') // Lists and quotes
            .replace(/^#+\s/gm, ''); // Headers

        const newText = text.substring(0, start) + cleanText + text.substring(end);
        this.decodedContent = newText;
        const outputValue = this.encodeContent(newText);
        this.content = outputValue;
        this.contentChange.emit(outputValue);
        this.updatePreview();
        this.addToHistory(this.decodedContent);
    }

    clearText() {
        this.decodedContent = '';
        this.content = '';
        this.contentChange.emit('');
        this.markdownPreview = this.sanitizer.sanitize(1, '<em class="text-gray-400">Nothing to preview</em>') || '';
        this.collapsedPreview = this.sanitizer.sanitize(1, this.collapsedPlaceholder) || '';
        this.history = [];
        this.historyIndex = -1;
        this.updateHistoryButtons();
    }

    // Close all popups when clicking outside
    closeAllPopups() {
        this.showTableSelector = false;
        this.showHighlightColorPicker = false;
        this.showTextColorPicker = false;
    }

    onBlur() {
        setTimeout(() => {
            this.onBlurEvent.emit(this.content);
        }, 500);
    }
}
